/**
 * Who you referred and what each of them is worth. Emails are masked server-side
 * — a partner needs to see that a signup converted, not who it was.
 */
import { useQuery } from '@tanstack/react-query';
import { classNames } from '~/utils/classNames';
import { apiGetMyReferrals, type AffiliateReferral } from '~/lib/api/affiliates';
import { Cell, CellGrid, EmptyState, Panel, PageHead, fmtDate, money, rate, usePortal } from './shared';

export default function Referrals() {
  const { data } = usePortal();
  const { funnel } = data;

  const query = useQuery({
    queryKey: ['affiliate', 'referrals'],
    queryFn: apiGetMyReferrals,
  });

  const referrals = query.data ?? [];
  const converted = referrals.filter((r) => r.payments > 0).length;

  return (
    <div className="flex flex-col gap-7">
      <PageHead
        title="Referrals"
        subtitle="Everyone who signed up through your link. A referral keeps earning for you every time they pay inside their window."
      />

      <CellGrid columns={3}>
        <Cell label="Clicks" value={funnel?.clicks ?? 0} />
        <Cell
          label="Signups"
          value={referrals.length}
          note={`${rate(referrals.length, funnel?.clicks ?? 0)} of clicks`}
        />
        <Cell label="Converted" value={converted} note={`${rate(converted, referrals.length)} of signups`} />
      </CellGrid>

      <Panel title="Referred customers">
        {query.isLoading ? (
          <div className="h-32 animate-pulse" />
        ) : referrals.length === 0 ? (
          <EmptyState
            title="No referrals yet"
            body="Share your link and anyone who signs up after visiting it shows up here — usually within seconds."
          />
        ) : (
          <div className="divide-y divide-hairline">
            {referrals.map((r) => (
              <ReferralRow key={`${r.email}-${r.referredAt}`} referral={r} />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function ReferralRow({ referral }: { referral: AffiliateReferral }) {
  const windowOpen = new Date(referral.windowEndsAt).getTime() > Date.now();
  const converted = referral.payments > 0;

  return (
    // Phones stack the pill and amount under the email so the email isn't squeezed to ~100px.
    <div className="flex flex-col gap-2 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5">
      <div className="min-w-0">
        <div className="ui-mono truncate text-[13px] text-primary">{referral.email}</div>
        <div className="mt-0.5 text-[11.5px] text-muted">
          Joined {fmtDate(referral.referredAt)} ·{' '}
          {windowOpen ? `earning until ${fmtDate(referral.windowEndsAt)}` : 'window closed'}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-4">
        <span
          className={classNames('ui-pill', converted ? 'text-success border-success/30' : 'text-muted border-subtle')}
        >
          {converted ? `${referral.payments} payment${referral.payments === 1 ? '' : 's'}` : 'Free plan'}
        </span>
        <div
          className={classNames(
            'text-right text-[13.5px] font-medium tabular-nums sm:w-20',
            converted ? 'text-primary' : 'text-faint',
          )}
        >
          {money(referral.earnedCents)}
        </div>
      </div>
    </div>
  );
}
